import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { euro } from "@/lib/format";
import { PageHeader, StatCard, Loading } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Repeat, Euro, Users, PackageCheck, AlertTriangle, Building2, MapPin,
  Boxes, Wallet, Home, Store, TrendingUp,
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const isNet = user.role === "super_admin";

  useEffect(() => {
    api.get(isNet ? "/dashboard/network" : "/dashboard/partner").then((r) => setData(r.data));
  }, [isNet]);

  if (!data) return <Loading />;

  if (isNet)
    return (
      <div>
        <PageHeader title="Dashboard Network" subtitle="Panoramica dell'intero network CambioBiancheria.it" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Partner attivi" value={data.partners} icon={Building2} testId="stat-partners" />
          <StatCard label="Cambio Point" value={data.cambio_points} icon={MapPin} tone="cyan" testId="stat-cps" />
          <StatCard label="Clienti totali" value={data.customers_total} icon={Users} testId="stat-customers" sub={`${data.home} HOME · ${data.host} HOST`} />
          <StatCard label="Cambi oggi" value={data.exchanges_today} icon={Repeat} tone="cyan" testId="stat-ex-today" />
          <StatCard label="Cambi mese" value={data.exchanges_month} icon={TrendingUp} testId="stat-ex-month" />
          <StatCard label="Transato mese" value={euro(data.revenue_month_cents)} icon={Euro} tone="ok" testId="stat-revenue" />
          <StatCard label="Biancheria presso clienti" value={data.linen_at_customer} icon={Boxes} testId="stat-at-customer" />
          <StatCard label="Cauzioni trattenute" value={euro(data.deposits_held_cents)} icon={Wallet} tone="cyan" testId="stat-deposits" />
          <StatCard label="Anomalie aperte" value={data.open_anomalies} icon={AlertTriangle} tone={data.open_anomalies ? "warn" : "ok"} testId="stat-anomalies" />
          <StatCard label="Stock complessivo" value={data.total_stock} icon={Boxes} testId="stat-stock" />
        </div>

        <Card className="mt-6 p-6">
          <h3 className="font-display font-bold text-lg text-[#0B2447] mb-4">Classifica Partner</h3>
          <div className="space-y-2">
            {data.ranking.map((r, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-[#0B2447] text-white flex items-center justify-center text-sm font-bold">{i + 1}</span>
                  <div>
                    <p className="font-semibold text-[#0B2447]">{r.partner}</p>
                    <p className="text-xs text-slate-400">{r.city}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-[#0B2447]">{euro(r.revenue_cents)}</p>
                  <p className="text-xs text-slate-400">{r.exchanges} cambi</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Operatività di oggi al Cambio Point">
        <Button onClick={() => nav("/cambio")} className="bg-[#0EA5C4] hover:bg-[#0c8ba7]" data-testid="dash-new-exchange"><Repeat size={18} className="mr-2" />Nuovo Cambio</Button>
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Cambi oggi" value={data.exchanges_today} icon={Repeat} tone="cyan" testId="stat-ex-today" />
        <StatCard label="Ricavi oggi" value={euro(data.revenue_today_cents)} icon={Euro} tone="ok" testId="stat-revenue" />
        <StatCard label="Clienti attivi" value={data.active_customers} icon={Users} sub={`${data.home} HOME · ${data.host} HOST`} testId="stat-customers" />
        <StatCard label="Restituzioni da verificare" value={data.pending_returns} icon={PackageCheck} tone={data.pending_returns ? "warn" : "ok"} testId="stat-pending" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <StatCard label="Anomalie aperte" value={data.open_anomalies} icon={AlertTriangle} tone={data.open_anomalies ? "err" : "ok"} testId="stat-anomalies" />
        <div className="col-span-2 lg:col-span-1 flex gap-4">
          <StatCard label="HOME" value={data.home} icon={Home} testId="stat-home" />
        </div>
        <StatCard label="HOST" value={data.host} icon={Store} tone="cyan" testId="stat-host" />
      </div>

      <Card className="mt-6 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg text-[#0B2447]">Stock critico</h3>
          <Button variant="outline" size="sm" onClick={() => nav("/biancheria")}>Vedi Pool</Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.critical_stock.map((s, i) => (
            <div key={i} className={`p-4 rounded-xl border ${s.alert ? "border-orange-300 bg-orange-50" : "border-slate-200 bg-slate-50"}`} data-testid={`stock-${i}`}>
              <div className="flex items-center justify-between">
                <p className="font-semibold text-sm text-[#0B2447]">{s.article_name}</p>
                {s.alert && <AlertTriangle size={16} className="text-orange-500" />}
              </div>
              <p className="stat-num text-2xl mt-1 text-[#0B2447]">{s.clean}</p>
              <p className="text-xs text-slate-400">pulite disponibili {s.low_threshold_qty ? `· soglia ${s.low_threshold_qty}` : ""}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
